/**
 * 正式相册（/album，P3：用户 10-03「做正式的相册页」；c84a 多级页面单 L2 回忆「照片优先，像可翻看的收藏册」）。全屏二级页，自带 WorldGate，左上回 /memories。
 * - 读 pets.album（GET /pets/{pet_id}/album，纯读）：只有画好的照片——后端默认网格是已成图、没移除、不是幻想主题的，按拍摄时刻倒序、带游标翻页。
 *   在画的、没画成的、结果没确认的不在这里、也不叫照片：不提供独立拍照申请入口。
 * - 按 album_day（服务端按宠物日册时区算好的日子）分组，每组一行日子、下面一格格照片；点一张进详情（/album/:photoId）。
 *   往下有「翻看更早的照片」，取下一页（游标）；翻过的页留在缓存里，从详情回来不重取，滚动位置由外壳的滚动恢复接上。
 * - 「想象出来的冒险」（category=fantasy）单独一叠，默认收起，写明不算 TA 真的去过。
 * - 当前宠物走 useCurrentPet；查询键带账号与宠物，换宠物换 key、整本重读，旧宠物的照片不会落到新宠物名下。
 * - 空：一句温和的话和自然聊天入口；相册没开放（能力未接入）：温和说明；其余错误走统一错误态，可重试。看得见的字里不写 AI、模型、生成。
 * - 某几格的图读不出来（UI2-ALBUM-ERR-01）：格子里照旧一句「预览暂时没读到」、仍能点进详情；网格上方一条「有 N 张照片没显示出来」，
 *   带「重新读取」（重读这一本的照片信息：后端换了地址的那几格换新节点重新显示，地址没变的照实说还没读到）。提示条在格子外面，格子里不放播报。
 * - 玩家亲手点一次「重新读取」，读不出来的那几格也各换一个新节点、把同一张图再取一次（I 10-13 定方案 2，UI2-ALBUM-ERR-01-R1）；
 *   不自动重试，一次点击每格最多再取一次；取的时候提示条说「正在重新读取…」、按钮锁住，等这几格各自报回读到或没读到。
 */
import { useCallback, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { useInfiniteQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import type { PetAlbumPage, PetPhoto } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { serverNow } from "@/shared/time/clock";
import { Button, EMPTY_ART, EmptyArtImage, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { WorldGate } from "@/features/world_map/WorldGate";
import { useCurrentPet, type CurrentPet } from "./currentPet";
import { ALBUM_PAGE, albumBookKey, dayText, FANTASY_PAGE, groupByDay, photoLabel, placeLine, shown } from "./album";
import "./album.css";
import { AlbumRemovedNotice } from "./AlbumHide";

export function AlbumPage() {
  return (
    <WorldGate>
      <AlbumBody />
    </WorldGate>
  );
}

function AlbumBody() {
  const current = useCurrentPet();
  const pet = current.status === "ready" ? current.pet : null;
  return (
    <Page bare className="ps-album">
      {/* 顶栏用全站那一种（UI-BASE-01b，总账 V-03）：原来是无底返回＋20px 标题 */}
      <TopBar title={pet ? `${pet.name}的相册` : "相册"} subtitle="TA 带回来的照片，按日子收着。" sourceBack={{ fallback: "/memories", fallbackLabel: "返回回忆" }} />
      {/* 从详情页移除成功回来：顶上一句「已从相册移除」（WIRE-01 A7） */}
      <AlbumRemovedNotice />
      {current.status === "pending" ? (
        <LoadingState lines={3} label="正在找到 TA…" />
      ) : current.status === "error" ? (
        <ErrorState error={current.error} onRetry={current.retry} />
      ) : pet ? (
        <AlbumBook key={JSON.stringify([current.userId, pet.petId])} pet={pet} userId={current.userId} />
      ) : (
        <p className="ps-album-quiet" role="status">
          还没有住进来的伙伴。
        </p>
      )}
    </Page>
  );
}

type BookCategory = "default" | "fantasy";
type BookStack = { settled: boolean; fresh: boolean; busy: boolean };
type BookOwner = {
  userId: string | null; petId: string; mode: string; pets: ReturnType<typeof useServices>["pets"];
  client: QueryClient; epoch: number; stacks: Record<BookCategory, BookStack>;
};
type BookProof = { pets: BookOwner["pets"]; mode: string; book: string };
const bookProofs = new WeakMap<QueryClient, WeakMap<object, BookProof>>();
const bookLeases = new WeakMap<QueryClient, Map<string, BookOwner>>();
let nextBookEpoch = 0;
class BookScopeEnded extends Error { constructor() { super("Album book context ended"); } }
const stackKey = (scope: Pick<BookOwner, "userId" | "petId">, category: BookCategory) => albumBookKey(scope.userId ?? "-", scope.petId, category);
function newBookOwner(userId: string | null, petId: string, pets: BookOwner["pets"], client: QueryClient): BookOwner {
  const stack = (category: BookCategory): BookStack => {
    const key = albumBookKey(userId ?? "-", petId, category);
    const state = client.getQueryState(key);
    const data = state?.data;
    const proof = data && typeof data === "object" ? bookProofs.get(client)?.get(data) : undefined;
    // Only previously committed data from this exact service/client/mode may survive a detail return.
    const settled = Boolean(proof && proof.pets === pets && proof.mode === env.dataMode && state?.status === "success" && proof.book === JSON.stringify(key));
    return { settled, fresh: !settled, busy: false };
  };
  return { userId, petId, pets, client, mode: env.dataMode, epoch: ++nextBookEpoch, stacks: { default: stack("default"), fantasy: stack("fantasy") } };
}
function AlbumBook({ pet, userId }: { pet: CurrentPet; userId: string | null }) {
  const { pets } = useServices();
  const client = useQueryClient();
  const [scope, setScope] = useState(() => newBookOwner(userId, pet.petId, pets, client));
  const committed = useRef<BookOwner | null>(null);
  useLayoutEffect(() => {
    committed.current = scope;
    return () => { if (committed.current === scope) committed.current = null; };
  }, [scope]);
  if (scope.userId !== userId || scope.petId !== pet.petId || scope.pets !== pets || scope.client !== client || scope.mode !== env.dataMode) {
    setScope(newBookOwner(userId, pet.petId, pets, client));
    return null;
  }
  if (scope.mode === "live" && !userId?.trim()) return <AlbumEmpty name={pet.name} />;
  return <OwnedAlbumBook key={scope.epoch} scope={scope} pet={pet} userId={userId} isCurrent={() => committed.current === scope} />;
}
function useAlbumPages(scope: BookOwner, category: BookCategory, limit: number, isCurrent: () => boolean) {
  const lifetime = useRef({ active: false, generation: 0 });
  const key = stackKey(scope, category);
  const id = JSON.stringify(key);
  const stack = scope.stacks[category];
  const owns = (generation = lifetime.current.generation) => lifetime.current.active && lifetime.current.generation === generation && isCurrent();
  useLayoutEffect(() => {
    const mount = lifetime.current;mount.active = true;mount.generation++;
    let leases = bookLeases.get(scope.client);
    if (!leases) { leases = new Map();bookLeases.set(scope.client, leases); }
    leases.set(id, scope);
    return () => {
      mount.active = false;mount.generation++;
      if (leases.get(id) === scope) { leases.delete(id);void scope.client.cancelQueries({ queryKey: key, exact: true }); }
    };
  }, [scope, id]);
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: async ({ pageParam, signal }) => {
      const generation = lifetime.current.generation;
      if (!owns(generation)) throw new BookScopeEnded();
      try {
        const result = await scope.pets.album(scope.petId, { limit, cursor: pageParam, ...(category === "default" ? {} : { category }) }, signal);
        if (!owns(generation)) throw new BookScopeEnded();
        stack.settled = true;
        return result;
      } catch (error) {
        if (!owns(generation)) throw new BookScopeEnded();
        stack.settled = true;
        throw error;
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last: PetAlbumPage) => last.next_cursor ?? undefined,
    enabled: scope.mode !== "live" || Boolean(scope.userId?.trim()),
    staleTime: 60_000,
    refetchOnMount: stack.fresh ? "always" : true,
    retry: (count, error) => {
      if (!owns() || error instanceof BookScopeEnded) return false;
      const retry = scope.client.getDefaultOptions().queries?.retry;
      return typeof retry === "function" ? retry(count, error) : retry === true || count < (typeof retry === "number" ? retry : retry === false ? 0 : 3);
    },
  });
  useLayoutEffect(() => {
    if (!owns()) return;
    let proofs = bookProofs.get(scope.client);
    if (!proofs) { proofs = new WeakMap();bookProofs.set(scope.client, proofs); }
    if (query.isError && query.data) proofs.delete(query.data);
    else if (stack.settled && query.data) proofs.set(query.data, { pets: scope.pets, mode: scope.mode, book: id });
  }, [scope, id, stack.settled, query.data, query.isError, query.isFetching]);
  const read = async (next: boolean) => {
    if (!owns() || stack.busy || scope.client.getQueryState(key)?.fetchStatus === "fetching") return;
    stack.busy = true;
    try { if (next) await query.fetchNextPage({ cancelRefetch: false });else await query.refetch({ cancelRefetch: false }); }
    finally { stack.busy = false; }
  };
  return { ...query, refetch: () => read(false), fetchNextPage: () => read(true) };
}

/** PetPhoto grids require finished media; the communicator historical-paper rule does not apply. */
const readyMedia = (photo: PetPhoto) => photo.generation.state === "ready" && shown(photo);

/** Exact current book only: cache permission events precede batched query observers. */
function useBookError(userId: string | null, petId: string, category: "default" | "fantasy") {
  const client = useQueryClient();
  const subscribe = useCallback((changed: () => void) => client.getQueryCache().subscribe(event => {
    const key = albumBookKey(userId ?? "-", petId, category);
    if (event.query.queryKey.length === key.length && event.query.queryKey.every((value: unknown, i: number) => value === key[i])) changed();
  }), [client, userId, petId, category]);
  const readError = useCallback(() => {
    const state = client.getQueryState(albumBookKey(userId ?? "-", petId, category));
    return state?.status === "error" ? state.error : null;
  }, [client, userId, petId, category]);
  return useSyncExternalStore(subscribe, readError, readError);
}

function OwnedAlbumBook({ scope, pet, userId, isCurrent }: { scope: BookOwner; pet: CurrentPet; userId: string | null; isCurrent: () => boolean }) {
  const book = useAlbumPages(scope, "default", ALBUM_PAGE, isCurrent);
  const fantasy = useAlbumPages(scope, "fantasy", FANTASY_PAGE, isCurrent);
  const observedBookError = useBookError(userId, pet.petId, "default");
  const observedFantasyError = useBookError(userId, pet.petId, "fantasy");
  const fantasyFailed = fantasy.isError || observedFantasyError !== null;
  // 读不出来的格子（按 Tile 的资源串记：账号、宠物、照片、地址都在里面，换了地址就是新的一格）
  const [failedTiles, setFailedTiles] = useState<ReadonlySet<string>>(() => new Set());
  // 玩家点了重新读取、正在重取的格子；每格重取过几次（换节点用）；点过没有（读完还没读到时照实说）
  const [retryingTiles, setRetryingTiles] = useState<ReadonlySet<string>>(() => new Set());
  const [attempts, setAttempts] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [retried, setRetried] = useState(false);
  const settle = useCallback((resource: string, ok: boolean) => {
    if (!isCurrent()) return;
    setRetryingTiles((old) => { if (!old.has(resource)) return old; const next = new Set(old); next.delete(resource); return next; });
    if (!ok) setFailedTiles((old) => (old.has(resource) ? old : new Set(old).add(resource)));
  }, [isCurrent]);
  const tiles: TileTracker = { attemptOf: (resource) => attempts.get(resource) ?? 0, settle };

  if (observedBookError !== null || book.isError) return <AlbumFailure error={observedBookError ?? book.error} onRetry={() => void book.refetch()} />;
  if (!scope.stacks.default.settled || book.isPending || !book.data) return <LoadingState lines={3} label="正在翻开相册…" />;
  const photos = book.data.pages.flatMap((page) => page.items).filter(photo => photo.pet_id === pet.petId && readyMedia(photo));
  const total = book.data.pages.some(page => page.items.some(photo => photo.pet_id !== pet.petId)) ? photos.length : book.data.pages[0]?.total_visible ?? photos.length;
  // An error is not permission to keep projecting cached private media or its count.
  const fantasyData = fantasyFailed || !scope.stacks.fantasy.settled ? undefined : fantasy.data;
  const imaginedPhotos = fantasyData ? fantasyData.pages.flatMap((page) => page.items).filter(photo => photo.pet_id === pet.petId && readyMedia(photo)) : [];
  const imaginedTotal = fantasyData?.pages.some(page => page.items.some(photo => photo.pet_id !== pet.petId)) ? imaginedPhotos.length : fantasyData?.pages[0]?.total_visible ?? 0;
  if (photos.length === 0 && imaginedPhotos.length === 0 && !book.hasNextPage && !fantasyFailed) return <AlbumEmpty name={pet.name} />;
  const thisYear = new Date(serverNow()).getFullYear();
  const shownResources = [...photos, ...imaginedPhotos].map((photo) => tileResource(userId, pet.petId, photo));
  const failedNow = shownResources.filter((resource) => failedTiles.has(resource)).length;
  const retryingNow = shownResources.filter((resource) => retryingTiles.has(resource)).length;
  const readAgain = () => {
    const targets = shownResources.filter((resource) => failedTiles.has(resource));
    const fantasyToo = imaginedPhotos.some((photo) => failedTiles.has(tileResource(userId, pet.petId, photo)));
    setAttempts((old) => { const next = new Map(old); for (const resource of targets) next.set(resource, (next.get(resource) ?? 0) + 1); return next; });
    setRetryingTiles(new Set(targets));
    setFailedTiles(new Set());
    setRetried(true);
    return Promise.all([book.refetch(), fantasyToo ? fantasy.refetch() : null]);
  };
  return (
    <>
      {total > 0 ? <p className="ps-album-count">一共 {total} 张</p> : null}
      {failedNow + retryingNow > 0 ? (
        <MediaRetry count={failedNow} retrying={retryingNow > 0} retried={retried} reading={book.isFetching || fantasy.isFetching} onRead={readAgain} />
      ) : null}
      <div className="ps-album-days">
        {groupByDay(photos).map((group) => (
          <DaySection key={group.day ?? "undated"} title={dayText(group.day, thisYear)} photos={group.photos} userId={userId} petId={pet.petId} tiles={tiles} />
        ))}
      </div>
      {book.hasNextPage ? (
        <div className="ps-album-more">
          {book.isFetchNextPageError ? <p role="status">更早的照片没翻到，再试一次。</p> : null}
          <button type="button" className="ps-btn ps-btn--ghost" disabled={book.isFetchingNextPage} onClick={() => void book.fetchNextPage()}>
            {book.isFetchingNextPage ? "正在往前翻…" : "翻看更早的照片"}
          </button>
        </div>
      ) : photos.length > 0 ? (
        <p className="ps-album-end">就到这里了</p>
      ) : null}
      {fantasyFailed ? (
        <section aria-label="想象冒险读取状态">
          <p className="ps-album-quiet">这叠想象冒险暂时没能读取。</p>
          <AlbumFailure error={observedFantasyError ?? fantasy.error} onRetry={() => void fantasy.refetch()} />
        </section>
      ) : null}
      {imaginedPhotos.length > 0 ? (
        <details className="ps-album-fantasy">
          <summary>
            <Icon name="sparkle" size={16} />
            想象出来的冒险
            <small>{imaginedTotal} 张 · 不算 TA 真的去过</small>
          </summary>
          <ul className="ps-album-grid">
            {imaginedPhotos.map((photo) => (
              <Tile key={photo.photo_id} photo={photo} userId={userId} petId={pet.petId} category="fantasy" tiles={tiles} />
            ))}
          </ul>
          {fantasy.hasNextPage ? (
            <div className="ps-album-more">
              <button type="button" className="ps-btn ps-btn--ghost" disabled={fantasy.isFetchingNextPage} onClick={() => void fantasy.fetchNextPage()}>
                {fantasy.isFetchingNextPage ? "正在往前翻…" : "再看几张"}
              </button>
            </div>
          ) : null}
        </details>
      ) : null}
    </>
  );
}

/**
 * 网格上方那一条：有几格的图没读出来、点「重新读取」重读照片信息，并把读不出来的那几格各重取一次。
 * 重取的时候说「正在重新读取…」、按钮锁住；取完还有读不到的，照实说还没读到、过一会儿再来。只有这一条播报（role=status），格子里不放。
 */
function MediaRetry({ count, retrying, retried, reading, onRead }: { count: number; retrying: boolean; retried: boolean; reading: boolean; onRead: () => Promise<unknown> }) {
  const busy = retrying || reading;
  return (
    <div className="ps-album-more ps-album-retry">
      <p role="status">{retrying ? "正在重新读取…" : retried && !reading ? `还有 ${count} 张没读到，过一会儿再来看看。` : `有 ${count} 张照片没显示出来。`}</p>
      <Button variant="ghost" icon="refresh" loading={busy} onClick={() => { if (!busy) void onRead().catch(() => undefined); }}>
        重新读取
      </Button>
    </div>
  );
}

/** 格子报回读到还是没读到；第几次重取（换节点用）。由 OwnedAlbumBook 持有。 */
type TileTracker = { attemptOf: (resource: string) => number; settle: (resource: string, ok: boolean) => void };

/** 一格媒体资源的身份（和 Tile 里重建媒体用的是同一串）：账号、宠物、照片、种类、地址、成图状态 */
function tileResource(userId: string | null, petId: string, photo: PetPhoto): string {
  return JSON.stringify([userId, petId, photo.pet_id, photo.photo_id, photo.asset.kind, photo.asset.media_url, photo.generation.state]);
}

function DaySection({ title, photos, userId, petId, tiles }: { title: string; photos: PetPhoto[]; userId: string | null; petId: string; tiles: TileTracker }) {
  return (
    <section className="ps-album-day" aria-label={title}>
      <h2>{title}</h2>
      <ul className="ps-album-grid">
        {photos.map((photo) => (
          <Tile key={photo.photo_id} photo={photo} userId={userId} petId={petId} tiles={tiles} />
        ))}
      </ul>
    </section>
  );
}

/** 一格保留原详情链接；媒体读取状态属于当前账号、宠物和具体资源。 */
function Tile({ photo, userId, petId, category = "default", tiles }: { photo: PetPhoto; userId: string | null; petId: string; category?: "default" | "fantasy"; tiles: TileTracker }) {
  const client = useQueryClient();
  const place = placeLine(photo);
  const descriptionId = useId();
  const resource = tileResource(userId, petId, photo);
  const canRead = () => {
    const current = client.getQueryState<{ pages: PetAlbumPage[] }>(albumBookKey(userId ?? "-", petId, category));
    if (!current || current.status === "error") return false;
    return current.data?.pages.some(page => page.items.some(item => readyMedia(item)
      && item.pet_id === photo.pet_id && item.photo_id === photo.photo_id
      && item.asset.kind === photo.asset.kind && item.asset.media_url === photo.asset.media_url)) ?? false;
  };
  return (
    <li className="ps-album-tile">
      <Link to={`/album/${encodeURIComponent(photo.photo_id)}`} aria-label={photoLabel(photo)} aria-describedby={descriptionId} data-testid="album-tile">
        <span className="ps-album-tile__frame">
          {/* 玩家点重新读取时，读不出来的格子换一个新节点（key 带重取次数）；不点不重取 */}
          <TileMedia key={`${resource}#${tiles.attemptOf(resource)}`} photo={photo} descriptionId={descriptionId} canRead={canRead} onSettled={(ok) => tiles.settle(resource, ok)} />
        </span>
        {place ? <span className="ps-album-tile__place">{place}</span> : null}
      </Link>
    </li>
  );
}

/** 换资源仅重建媒体，保留仍有效的链接焦点；旧节点和同 URL 的失败不会自动重试。 */
function TileMedia({ photo, descriptionId, canRead, onSettled }: { photo: PetPhoto; descriptionId: string; canRead: () => boolean; onSettled: (ok: boolean) => void }) {
  const [state, setState] = useState<"pending" | "available" | "failed">("pending");
  const mounted = useRef(false);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const update = (node: HTMLImageElement | HTMLVideoElement, next: "available" | "failed") => {
    if (!mounted.current || !node.isConnected || !canRead()) return;
    setState(previous => previous === "failed" ? previous : next);
    onSettled(next === "available");
  };
  const video = photo.asset.kind === "video";
  const description = state === "failed"
    ? "预览暂时没读到，可进入详情查看"
    : state === "pending"
      ? video ? "视频预览待加载" : "正在读取照片预览"
      : video ? "视频预览帧已读取，进入详情查看" : "照片预览已读取";
  return (
    <>
      {state !== "failed" ? video ? (
        <video src={photo.asset.media_url!} muted playsInline preload="metadata" aria-hidden="true"
          onLoadedData={event => update(event.currentTarget, "available")}
          onError={event => update(event.currentTarget, "failed")} />
      ) : (
        <img src={photo.asset.media_url!} alt="" loading="lazy"
          onLoad={event => update(event.currentTarget, "available")}
          onError={event => update(event.currentTarget, "failed")} />
      ) : null}
      <span id={descriptionId} className={state === "available" ? "ps-album-tile__description" : "ps-album-tile__media-note"}>
        {description}
      </span>
      {video && state === "available" ? (
        <span className="ps-album-tile__play" aria-hidden="true"><Icon name="play" size={16} /></span>
      ) : null}
    </>
  );
}

function AlbumEmpty({ name }: { name: string }) {
  return (
    <div className="ps-album-note" role="status">
      {/* 空相册插图（ASSET-UI-010，UI-EMPTY-ART-01）替掉原来的图标圆片；打不开、找不到那两种照旧用图标 */}
      <EmptyArtImage art={EMPTY_ART.album} className="ps-album-note__art" />
      <h2>{name}的相册还空着</h2>
      {/* UI2-REVIEW110-01（I 10-14 夜间实机评审）：原来「已有照片会按日子收在这里；现在没有可查看的照片」前后矛盾，标题已说空着，这里只说照片会收在哪 */}
      <p>TA 拍的照片会按日子收在这里。</p>
      <Link className="ps-btn ps-btn--ghost" to="/communicator">
        去和 TA 聊聊
      </Link>
    </div>
  );
}

/** 读不到：相册没开放（能力未接入，含演示模式）温和说明、不编照片；其余走统一错误态，可重试。 */
function AlbumFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (isApiError(error) && error.isCapabilityUnavailable) {
    return (
      <div className="ps-album-note" role="note">
        <span className="ps-album-note__icon" aria-hidden="true">
          <Icon name="album" size={22} />
        </span>
        <h2>相册暂时打不开</h2>
        <p>过一会儿再来看看。</p>
      </div>
    );
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
